import type { ElementType } from 'react';
import { Box, Tooltip, Button, Flex } from '@chakra-ui/react';
import type { Editor } from '@tiptap/react';
import { RiListOrdered, RiListUnordered } from 'react-icons/ri';
// import { ImageButton } from '@/ui/editor/ImageButton';
import { Link } from '@/ui/editor/Link';
import {
  AlignLeft,
  AlignRight,
  Bold01,
  HeadingLevel01,
  HeadingLevel02,
  HeadingLevel03,
  HeadingLevel04,
  Italic01,
  Link01,
  LinkBroken01,
  Pilcrow01,
  Strikethrough03,
} from '@kula-ai/icons-core';

const UnLink = ({ editor }: { editor?: Editor }) => (
  <Button
    title="Unlink"
    bg="light.background"
    pb="19px"
    pl="10px"
    _hover={{ bg: 'light.background' }}
    onClick={() => editor?.chain()?.focus()?.unsetLink()?.run()}
  >
    <LinkBroken01
      size="16px"
      pb="20px"
      color={editor?.isActive('link') ? '#121314' : '#aaa'}
    />
  </Button>
);

interface ToolbarCTA {
  title: string;
  icon: ElementType;
  onClick?: () => void;
  Component?: ElementType;
}

function Toolbar({ editor }: { editor?: Editor }) {
  const toolbarCTAs: ToolbarCTA[] = [
    {
      title: 'Bold',
      icon: Bold01,
      onClick: () => editor?.chain()?.focus()?.toggleBold()?.run(),
    },
    {
      title: 'Italic',
      icon: Italic01,
      onClick: () => editor?.chain()?.focus()?.toggleItalic()?.run(),
    },
    {
      title: 'Strike',
      icon: Strikethrough03,
      onClick: () => editor?.chain()?.focus()?.toggleStrike()?.run(),
    },
    {
      title: '',
      icon: HeadingLevel01,
      onClick: () =>
        editor?.chain()?.focus()?.toggleHeading({ level: 1 })?.run(),
    },
    {
      title: '',
      icon: HeadingLevel02,
      onClick: () =>
        editor?.chain()?.focus()?.toggleHeading({ level: 2 })?.run(),
    },
    {
      title: '',
      icon: HeadingLevel03,
      onClick: () =>
        editor?.chain()?.focus()?.toggleHeading({ level: 3 })?.run(),
    },
    {
      title: '',
      icon: HeadingLevel04,
      onClick: () =>
        editor?.chain()?.focus()?.toggleHeading({ level: 4 })?.run(),
    },
    {
      title: 'Align Left',
      icon: AlignLeft,
      onClick: () => editor?.commands?.setTextDirection?.('ltr'),
    },
    {
      title: 'Align Right',
      icon: AlignRight,
      onClick: () => editor?.commands?.setTextDirection?.('rtl'),
    },
    {
      title: 'Ordered List',
      icon: RiListOrdered,
      onClick: () => editor?.chain()?.focus()?.toggleOrderedList()?.run(),
    },
    {
      title: 'Unordered List',
      icon: RiListUnordered,
      onClick: () => editor?.chain()?.focus()?.toggleBulletList()?.run(),
    },
    {
      title: 'Paragraph',
      icon: Pilcrow01,
      onClick: () => editor?.chain()?.focus()?.setParagraph()?.run(),
    },
    {
      title: 'Insert Link',
      icon: Link01,
      Component: Link,
    },
    {
      title: 'UnLink',
      icon: LinkBroken01,
      Component: UnLink,
    },
    // {
    //   title: 'Insert Image',
    //   // icon: InsertImage,
    //   Component: ImageButton,
    // },
    // {
    //   title: 'Attach',
    //   icon: Attach,
    //   onClick: () => editor.chain().focus().toggleBold().run(), //TODO create a Attachment component
    // },
  ];

  const renderButton = ({
    title,
    icon: IconComponent,
    onClick,
    Component,
  }: ToolbarCTA) => {
    return (
      <Tooltip
        label={title}
        arrowSize={8}
        hasArrow
        placement="top"
        borderRadius="4px"
      >
        {Component ? (
          <Component icon={IconComponent} editor={editor} />
        ) : (
          <Button
            onClick={onClick}
            bg="light.background"
            px="5px !important"
            py="0px !important"
            _hover={{
              bg: 'light.background',
            }}
          >
            <Box pb="5px">
              <IconComponent color="#121314" size="16px" pb="20px" />
            </Box>
          </Button>
        )}
      </Tooltip>
    );
  };

  return (
    <Flex flexWrap="wrap">
      {toolbarCTAs.map((action) => renderButton(action))}{' '}
    </Flex>
  );
}

export default Toolbar;
